import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { TypeBadge } from "../components/TypeBadge";

type TrashTab = "media" | "ingest";

export function Trash() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TrashTab>("media");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["trash"], queryFn: () => api.listTrash({ limit: 200 }) });
  const ingestQ = useQuery({
    queryKey: ["ingest-trash"],
    queryFn: () => api.listIngestTrash({ limit: 200 }),
  });

  const restoreOne = useMutation({
    mutationFn: ({ id }: { id: number }) => api.restoreFromTrash(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trash"] });
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["favorites"] });
    },
  });

  const purgeOne = useMutation({
    mutationFn: ({ id }: { id: number }) => api.hardDeleteFromTrash(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trash"] }),
  });

  const bulkRestore = useMutation({
    mutationFn: (ids: number[]) => api.bulkRestoreTrash(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trash"] });
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["favorites"] });
      setSelected(new Set());
    },
  });

  const bulkPurge = useMutation({
    mutationFn: (ids: number[]) => api.bulkPurgeTrash(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trash"] });
      setSelected(new Set());
    },
  });

  const restoreIngestOne = useMutation({
    mutationFn: ({ id }: { id: number }) => api.restoreIngestTrash(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ingest-trash"] });
      qc.invalidateQueries({ queryKey: ["ingest"] });
    },
  });
  const purgeIngestOne = useMutation({
    mutationFn: ({ id }: { id: number }) => api.purgeIngestTrash(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingest-trash"] }),
  });
  const bulkRestoreIngest = useMutation({
    mutationFn: (ids: number[]) => api.bulkRestoreIngestTrash(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ingest-trash"] });
      qc.invalidateQueries({ queryKey: ["ingest"] });
      setSelected(new Set());
    },
  });
  const bulkPurgeIngest = useMutation({
    mutationFn: (ids: number[]) => api.bulkPurgeIngestTrash(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ingest-trash"] });
      setSelected(new Set());
    },
  });

  const purgeAllMedia = useMutation({
    mutationFn: () => api.purgeAllMediaTrash(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trash"] });
      setSelected(new Set());
    },
  });
  const purgeAllIngest = useMutation({
    mutationFn: () => api.purgeAllIngestTrash(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ingest-trash"] });
      setSelected(new Set());
    },
  });

  if (q.isPending) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (q.isError) return <div className="p-8 text-red-300">Error: {String(q.error)}</div>;
  const items = q.data.items;
  const ingestItems = ingestQ.data?.items ?? [];
  const ingestTotal = ingestQ.data?.total ?? 0;
  const ids = Array.from(selected);

  function toggleId(id: number, e?: React.MouseEvent) {
    e?.preventDefault();
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  // Switching tabs clears the selection — selected IDs aren't comparable
  // across the two lists (they refer to media.id vs ingest_findings.id).
  function setActiveTab(next: TrashTab): void {
    if (next === tab) return;
    setSelected(new Set());
    setTab(next);
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <a href="/admin" className="text-sm text-zinc-400 hover:text-zinc-200">
            ← back to admin
          </a>
          <h2 className="mt-1 text-2xl font-semibold">Trash</h2>
          <p className="text-xs text-zinc-500">
            {tab === "media" ? (
              <>
                {q.data.total} soft-deleted item{q.data.total === 1 ? "" : "s"}. Files live under{" "}
                <code>/media/.media-viewer/trash/</code>. Restore moves them back to their original
                path; hard-delete removes the file and the database row.
              </>
            ) : (
              <>
                {ingestTotal} item{ingestTotal === 1 ? "" : "s"} deleted from the ingest queue.
                Files live under <code>/media/.media-viewer/trash/ingest-deleted/</code>. Restore
                puts the file back in <code>/data/ingest</code> for re-processing on the next run.
              </>
            )}
          </p>
          <div className="mt-3 flex gap-1 text-xs">
            <button
              onClick={() => setActiveTab("media")}
              className={
                "rounded px-3 py-1 " +
                (tab === "media"
                  ? "bg-zinc-800 text-zinc-100"
                  : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800")
              }
            >
              Media ({q.data.total})
            </button>
            <button
              onClick={() => setActiveTab("ingest")}
              className={
                "rounded px-3 py-1 " +
                (tab === "ingest"
                  ? "bg-zinc-800 text-zinc-100"
                  : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800")
              }
            >
              Ingest ({ingestTotal})
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {ids.length > 0 && (
            <span className="text-zinc-300">
              {ids.length} selected
              <button
                onClick={() => setSelected(new Set())}
                className="ml-2 text-xs text-zinc-400 underline hover:text-zinc-200"
              >
                Clear
              </button>
            </span>
          )}
          <button
            onClick={() => {
              const ids = tab === "media" ? items.map((i) => Number(i.id)) : ingestItems.map((i) => Number(i.id));
              setSelected(new Set(ids));
            }}
            className="rounded bg-zinc-800 px-3 py-1 text-xs hover:bg-zinc-700"
          >
            Select all
          </button>
          <button
            onClick={async () => {
              if (ids.length === 0) return;
              setBusy("Restoring…");
              if (tab === "media") await bulkRestore.mutateAsync(ids);
              else await bulkRestoreIngest.mutateAsync(ids);
              setBusy(null);
            }}
            disabled={
              ids.length === 0 ||
              bulkRestore.isPending ||
              bulkRestoreIngest.isPending
            }
            className="rounded bg-emerald-700 px-3 py-1 text-emerald-100 hover:bg-emerald-600 disabled:opacity-50"
          >
            ↩ Restore selected
          </button>
          <button
            onClick={async () => {
              if (ids.length === 0) return;
              if (
                !window.confirm(
                  `Permanently delete ${ids.length} item${ids.length === 1 ? "" : "s"}? This cannot be undone.`,
                )
              ) {
                return;
              }
              setBusy("Purging…");
              if (tab === "media") await bulkPurge.mutateAsync(ids);
              else await bulkPurgeIngest.mutateAsync(ids);
              setBusy(null);
            }}
            disabled={ids.length === 0 || bulkPurge.isPending || bulkPurgeIngest.isPending}
            className="rounded bg-rose-800/60 px-3 py-1 text-rose-100 hover:bg-rose-800 disabled:opacity-50"
          >
            🗑 Hard-delete selected
          </button>
          {(tab === "media" ? q.data.total : ingestTotal) > 0 && (
            <button
              onClick={async () => {
                const total = tab === "media" ? q.data.total : ingestTotal;
                if (
                  !window.confirm(
                    `Permanently delete ALL ${total.toLocaleString()} item${
                      total === 1 ? "" : "s"
                    } in ${tab === "media" ? "media" : "ingest"} trash? This cannot be undone.`,
                  )
                ) {
                  return;
                }
                setBusy(`Purging all ${total.toLocaleString()}…`);
                try {
                  if (tab === "media") {
                    const r = await purgeAllMedia.mutateAsync();
                    setBusy(`Purged ${r.purgedRows.toLocaleString()} rows; file cleanup runs in background.`);
                  } else {
                    const r = await purgeAllIngest.mutateAsync();
                    setBusy(`Purged ${r.purgedFiles.toLocaleString()} files.`);
                  }
                  // Block the busy banner until the list actually reflects
                  // the change, so the user sees something real instead of
                  // "still showing all the assets" right after clicking.
                  await Promise.all([
                    qc.refetchQueries({ queryKey: ["trash"] }),
                    qc.refetchQueries({ queryKey: ["ingest-trash"] }),
                  ]);
                } catch (err) {
                  setBusy(`Purge all failed: ${String(err)}`);
                  return;
                }
                // Brief pause so the success message is readable.
                setTimeout(() => setBusy(null), 1500);
              }}
              disabled={purgeAllMedia.isPending || purgeAllIngest.isPending}
              className="rounded bg-rose-900 px-3 py-1 text-rose-100 hover:bg-rose-800 disabled:opacity-50"
              title="Hard-delete every item in this trash, regardless of selection or pagination"
            >
              💥 Purge all ({(tab === "media" ? q.data.total : ingestTotal).toLocaleString()})
            </button>
          )}
        </div>
      </div>

      {busy && <div className="mb-3 rounded bg-zinc-800 px-3 py-2 text-xs text-zinc-300">{busy}</div>}

      {tab === "ingest" ? (
        ingestItems.length === 0 ? (
          <div className="rounded border border-zinc-800 bg-zinc-900 p-8 text-center text-zinc-500">
            No ingest-deleted items.
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-left text-xs uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="w-8 px-3 py-2"></th>
                  <th className="px-3 py-2">Path</th>
                  <th className="px-3 py-2">Size</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Deleted</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {ingestItems.map((it) => {
                  const id = Number(it.id);
                  const isSelected = selected.has(id);
                  return (
                    <tr
                      key={id}
                      className={
                        "border-t border-zinc-800/60 " +
                        (isSelected ? "bg-emerald-900/20" : "")
                      }
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleId(id)}
                        />
                      </td>
                      <td className="break-all px-3 py-2 font-mono text-xs text-zinc-300">
                        {it.ingest_path}
                        {!it.trash_path_exists && (
                          <span className="ml-2 rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">
                            file gone
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-zinc-400">{formatBytes(Number(it.size_bytes))}</td>
                      <td className="px-3 py-2 text-xs text-zinc-500">
                        {it.kind}
                        {it.reason ? ` · ${it.reason}` : ""}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-500">
                        {it.resolved_at ? new Date(it.resolved_at).toLocaleString() : "—"}
                        {it.resolved_by_name && (
                          <span className="ml-1 text-zinc-600">by {it.resolved_by_name}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => restoreIngestOne.mutate({ id })}
                            disabled={!it.trash_path_exists || restoreIngestOne.isPending}
                            className="rounded bg-emerald-700 px-2 py-1 text-xs text-emerald-100 hover:bg-emerald-600 disabled:opacity-40"
                            title={
                              it.trash_path_exists
                                ? "Restore file to /data/ingest"
                                : "File no longer exists in trash"
                            }
                          >
                            ↩ Restore
                          </button>
                          <button
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Permanently delete ${it.ingest_path}? This cannot be undone.`,
                                )
                              ) {
                                purgeIngestOne.mutate({ id });
                              }
                            }}
                            disabled={purgeIngestOne.isPending}
                            className="rounded bg-rose-800/60 px-2 py-1 text-xs text-rose-100 hover:bg-rose-800 disabled:opacity-40"
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : items.length === 0 ? (
        <div className="rounded border border-zinc-800 bg-zinc-900 p-8 text-center text-zinc-500">
          Trash is empty.
        </div>
      ) : (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
        >
          {items.map((it) => {
            const id = Number(it.id);
            const isSelected = selected.has(id);
            return (
              <div
                key={id}
                className={
                  "overflow-hidden rounded border bg-zinc-900 transition-colors " +
                  (isSelected ? "border-emerald-400 ring-2 ring-emerald-400/40" : "border-zinc-800")
                }
              >
                <a
                  href="#"
                  onClick={(e) => toggleId(id, e)}
                  className="group relative block aspect-video bg-zinc-800"
                >
                  <img
                    src={`/api/media/${id}/thumb?size=512`}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover opacity-75 transition-opacity group-hover:opacity-100"
                  />
                  <TypeBadge kind={it.kind} />
                  <div
                    className={
                      "absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded border " +
                      (isSelected
                        ? "border-emerald-300 bg-emerald-500 text-white"
                        : "border-zinc-300 bg-black/60 text-transparent")
                    }
                  >
                    ✓
                  </div>
                </a>
                <div className="p-3 text-xs text-zinc-400">
                  <div className="break-all font-mono text-zinc-300" title={it.relative_path}>
                    {it.relative_path}
                  </div>
                  <div className="mt-1">{formatBytes(Number(it.size_bytes))}</div>
                  <div className="mt-1">
                    deleted {new Date(it.deleted_at).toLocaleString()}
                    {it.deleted_by_name && <span className="ml-1 text-zinc-500">by {it.deleted_by_name}</span>}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => restoreOne.mutate({ id })}
                      disabled={restoreOne.isPending}
                      className="flex-1 rounded bg-emerald-700 px-2 py-1 text-emerald-100 hover:bg-emerald-600 disabled:opacity-50"
                    >
                      ↩ Restore
                    </button>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Permanently delete ${it.relative_path}? This cannot be undone.`,
                          )
                        ) {
                          purgeOne.mutate({ id });
                        }
                      }}
                      disabled={purgeOne.isPending}
                      className="flex-1 rounded bg-rose-800/60 px-2 py-1 text-rose-100 hover:bg-rose-800 disabled:opacity-50"
                    >
                      🗑 Hard-delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatBytes(b: number): string {
  if (!Number.isFinite(b)) return "?";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
